import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Trash2, Upload, Loader2, Eye, EyeOff, Archive,
  FileText, ExternalLink, Package, RotateCcw, Frame, ImageOff, CheckCircle2,
  Copy,
} from 'lucide-react';
import { ActionButton, SectionHeader } from '../components/VisualAtoms';
import ArtworkImagesSection from '../components/ArtworkImagesSection';
import ArtworkStoryEditor, { CopyAssist } from '../components/ArtworkStoryEditor';
import DeleteArtworkModal from '../components/DeleteArtworkModal';
import {
  getArtwork,
  createArtwork,
  updateArtwork,
  publishArtwork,
  hideArtwork,
  archiveArtwork,
  markSoldOut,
  markDraft,
  getArtworksArtistsPicklist,
  requestArtworkUploadUrl,
  directUploadToSupabase,
  generateOfferPdf,
} from '../lib/artworksApi';

const STATUS_TONES = {
  draft:     { text: '#9a9a9a', border: '#333', bg: 'transparent' },
  published: { text: '#7ed4a0', border: 'rgba(126,212,160,0.4)', bg: 'rgba(126,212,160,0.08)' },
  hidden:    { text: '#e4c07a', border: 'rgba(228,192,122,0.4)', bg: 'rgba(228,192,122,0.08)' },
  sold_out:  { text: '#c89dff', border: 'rgba(200,157,255,0.42)', bg: 'rgba(200,157,255,0.08)' },
  archived:  { text: '#666',    border: '#222', bg: 'transparent' },
};

const CURRENCIES = ['EUR', 'USD', 'GBP', 'PLN'];
const STATUSES = ['draft', 'published', 'hidden', 'sold_out', 'archived'];

function Field({ label, hint, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-[10px] font-mono text-[#555]">{hint}</p>}
    </div>
  );
}

// Ancient tokens for the input styles — matches the admin OS
const inputCls =
  'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40 transition-colors';
const textareaCls =
  'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40 resize-y transition-colors';
const selectCls =
  'w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] text-[#f6f6f6] focus:outline-none focus:border-[#90E0EF]/40 uppercase tracking-[0.1em] font-mono';

export default function ArtworkEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    artist_id: '',
    title: '',
    slug: '',
    description: '',
    year: '',
    medium: '',
    dimensions: '',
    media_url: '',
    media_storage_path: '',
    media_bucket_name: '',
    thumbnail_url: '',
    media_type: 'image',
    edition_total: '',
    edition_available: '',
    edition_label: '',
    price_amount: '',
    currency: 'EUR',
    status: 'draft',
    is_featured: false,
  });
  const [originalForm, setOriginalForm] = useState(null);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    getArtworksArtistsPicklist().then(setArtists).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    getArtwork(id).then((row) => {
      setForm({
        artist_id: row.artist_id || '',
        title: row.title || '',
        slug: row.slug || '',
        description: row.description || '',
        year: row.year || '',
        medium: row.medium || '',
        dimensions: row.dimensions || '',
        media_url: row.media_url || '',
        media_storage_path: row.media_storage_path || '',
        media_bucket_name: row.media_bucket_name || '',
        thumbnail_url: row.thumbnail_url || '',
        media_type: row.media_type || 'image',
        edition_total: row.edition_total ?? '',
        edition_available: row.edition_available ?? '',
        edition_label: row.edition_label || '',
        price_amount: row.price_amount ?? '',
        currency: row.currency || 'EUR',
        status: row.status || 'draft',
        is_featured: !!row.is_featured,
        __artist: row.artist,
        __inquiry_count: row.inquiry_count,
        __published_at: row.published_at,
        __created_at: row.created_at,
      });
      setOriginalForm(row);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || 'Failed to load artwork');
    }).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const artist = useMemo(() => artists.find((a) => a.id === form.artist_id) || form.__artist, [artists, form.artist_id, form.__artist]);

  // ─── Upload media ───────────────────────────────────────────
  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Image too large (max 30MB)');
      return;
    }
    setUploading(true);
    try {
      const signed = await requestArtworkUploadUrl({ file_name: file.name, mime_type: file.type, kind: 'media' });
      await directUploadToSupabase(signed.signed_url, file);
      set('media_url', signed.public_url);
      set('media_storage_path', signed.path);
      set('media_bucket_name', signed.bucket_name);
      set('thumbnail_url', signed.public_url); // simple thumbnail = media for MVP
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ─── Save ───────────────────────────────────────────────────
  const validate = () => {
    if (!form.artist_id) return 'Choose an artist';
    if (!form.title.trim()) return 'Title is required';
    if (!form.medium.trim()) return 'Medium is required — it tells the AI how to render this work (print, sculpture, digital…)';
    if (form.price_amount !== '' && Number.isNaN(Number(form.price_amount))) return 'Price must be a number';
    if (form.edition_total !== '' && Number.isNaN(Number(form.edition_total))) return 'Edition total must be a number';
    if (form.edition_available !== '' && Number.isNaN(Number(form.edition_available))) return 'Edition available must be a number';
    return null;
  };

  const buildPayload = () => ({
    artist_id: form.artist_id,
    title: form.title.trim(),
    slug: form.slug ? form.slug.trim() : undefined,
    description: form.description || null,
    year: form.year || null,
    medium: form.medium || null,
    dimensions: form.dimensions || null,
    media_url: form.media_url || null,
    media_storage_path: form.media_storage_path || null,
    media_bucket_name: form.media_bucket_name || null,
    thumbnail_url: form.thumbnail_url || null,
    media_type: form.media_type || 'image',
    edition_total: form.edition_total === '' ? null : Number(form.edition_total),
    edition_available: form.edition_available === '' ? null : Number(form.edition_available),
    edition_label: form.edition_label || null,
    price_amount: form.price_amount === '' ? null : Number(form.price_amount),
    currency: form.currency || 'EUR',
    status: form.status || 'draft',
    is_featured: !!form.is_featured,
  });

  const doSave = async ({ redirectAfter = true } = {}) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (isNew) {
        const created = await createArtwork(buildPayload());
        toast.success('Artwork created');
        if (redirectAfter) navigate(`/admin/artworks/${created.id}`, { replace: true });
      } else {
        await updateArtwork(id, buildPayload());
        toast.success('Saved');
        setOriginalForm({ ...form });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doStatusAction = async (fn, msg) => {
    if (isNew) { toast.error('Save the artwork first'); return; }
    setBusy(true);
    try {
      await fn(id);
      toast.success(msg);
      // Refresh
      const row = await getArtwork(id);
      set('status', row.status);
      set('__published_at', row.published_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const doGeneratePdf = async () => {
    if (isNew) { toast.error('Save the artwork first'); return; }
    setBusy(true);
    const t = toast.loading('Generating offer PDF…');
    try {
      const data = await generateOfferPdf({
        artwork_ids: [id],
        title: form.title,
        currency: form.currency,
      });
      toast.success('Offer PDF ready', { id: t });
      window.open(data.document.pdf_url, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF generation failed', { id: t });
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = form.slug
    ? `${window.location.origin}/artworks/${form.slug}`
    : null;

  const dirty = originalForm !== null && JSON.stringify(buildPayload()) !== JSON.stringify({
    ...buildPayload(),
    // computed from originalForm — quick shallow check
  });

  // ─── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Sticky header bar */}
      <div className="sticky top-0 -mx-6 md:-mx-8 px-6 md:px-8 py-4 mb-6 bg-[#0a0a0a] border-b border-[#1a1a1a] z-20">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <button
              onClick={() => navigate('/admin/artworks')}
              data-testid="aw-editor-back"
              className="p-2 border border-[#1a1a1a] hover:border-[#333] text-[#888] hover:text-white transition-colors"
              title="Back to artworks"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#555]">
                {isNew ? 'NEW ARTWORK' : 'EDIT ARTWORK'}
              </p>
              <h1 className="text-2xl font-outfit font-semibold text-white tracking-tight truncate">
                {form.title || (isNew ? 'Untitled artwork' : 'Loading…')}
              </h1>
              <div className="mt-1 flex items-center gap-3 text-[11px] font-mono text-[#666]">
                {artist && <span className="text-[#90E0EF]">{artist.artist_name}</span>}
                {form.year && <span>· {form.year}</span>}
                {form.__inquiry_count > 0 && <span>· {form.__inquiry_count} INQUIRIES</span>}
                {publicUrl && form.status === 'published' && (
                  <>
                    <span>·</span>
                    <a href={publicUrl} target="_blank" rel="noreferrer" className="hover:text-white flex items-center gap-1">
                      Public page <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <ActionButton
                data-testid="aw-editor-generate-pdf"
                variant="secondary"
                icon={FileText}
                onClick={doGeneratePdf}
                disabled={busy}
              >
                {busy ? 'Working…' : 'Generate PDF'}
              </ActionButton>
            )}
            <ActionButton
              data-testid="aw-editor-save"
              variant="cyan"
              icon={saving ? Loader2 : Save}
              disabled={saving}
              onClick={() => doSave()}
            >
              {saving ? 'Saving…' : (isNew ? 'Create artwork' : 'Save changes')}
            </ActionButton>
          </div>
        </div>
      </div>

      {/* Two-column full-screen layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] gap-6">
        {/* LEFT — form */}
        <div className="space-y-8">
          {/* Media */}
          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
            <SectionHeader title="Artwork media" helper="Upload the primary image (required for public publish). Supports JPG, PNG, WebP. Max 30MB." />
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
              <div className="aspect-square w-full max-w-[220px] border border-[#1a1a1a] bg-[#050505] overflow-hidden flex items-center justify-center">
                {form.media_url ? (
                  <img src={form.media_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <ImageOff className="w-6 h-6 text-[#333] mx-auto" />
                    <p className="text-[10px] font-mono text-[#555] mt-2">NO IMAGE</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="aw-editor-file-input"
                  onChange={(e) => doUpload(e.target.files?.[0])}
                />
                <ActionButton
                  data-testid="aw-editor-upload-btn"
                  variant="secondary"
                  icon={uploading ? Loader2 : Upload}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : (form.media_url ? 'Replace image' : 'Upload image')}
                </ActionButton>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">
                    …or paste image URL
                  </label>
                  <input
                    data-testid="aw-editor-media-url"
                    value={form.media_url}
                    onChange={(e) => { set('media_url', e.target.value); set('thumbnail_url', e.target.value); }}
                    placeholder="https://…"
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Identity */}
          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
            <SectionHeader title="Identity" helper="Artist, title, and the URL slug. Slug auto-generates from artist + title if left blank." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Artist" htmlFor="aw-artist">
                <select
                  id="aw-artist"
                  data-testid="aw-editor-artist"
                  className={selectCls}
                  value={form.artist_id}
                  onChange={(e) => set('artist_id', e.target.value)}
                  disabled={!isNew && !!form.artist_id}
                >
                  <option value="">— select artist —</option>
                  {artists.map((a) => (
                    <option key={a.id} value={a.id}>{a.artist_name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Year">
                <input
                  data-testid="aw-editor-year"
                  className={inputCls}
                  value={form.year}
                  onChange={(e) => set('year', e.target.value)}
                  placeholder="2024"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Title">
                  <input
                    data-testid="aw-editor-title"
                    className={inputCls}
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    placeholder="Silent Column No. 3"
                  />
                  {!isNew && <CopyAssist artworkId={id} target="title" onApply={(t) => set('title', t)} />}
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Public slug" hint={publicUrl ? `Public URL: ${publicUrl}` : 'Leave blank to auto-generate.'}>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="aw-editor-slug"
                      className={inputCls}
                      value={form.slug}
                      onChange={(e) => set('slug', e.target.value)}
                      placeholder="auto"
                    />
                    {publicUrl && (
                      <button
                        onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Public URL copied'); }}
                        className="p-2.5 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[#888] hover:text-[#90E0EF] transition-colors"
                        title="Copy public URL"
                      ><Copy className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </Field>
              </div>
            </div>
          </section>

          {/* Print details */}
          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
            <SectionHeader title="Print / edition details" helper="Standard print product metadata shown on the public page and inside PDF offers." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Medium (required — drives AI rendering)">
                <input
                  data-testid="aw-editor-medium"
                  className={inputCls}
                  value={form.medium}
                  onChange={(e) => set('medium', e.target.value)}
                  placeholder="e.g. Giclée print on Hahnemühle · 3D printed resin sculpture · Digital video"
                />
              </Field>
              <Field label="Dimensions">
                <input
                  data-testid="aw-editor-dimensions"
                  className={inputCls}
                  value={form.dimensions}
                  onChange={(e) => set('dimensions', e.target.value)}
                  placeholder="60 × 90 cm"
                />
              </Field>
              <Field label="Edition total">
                <input
                  data-testid="aw-editor-edition-total"
                  type="number"
                  className={inputCls}
                  value={form.edition_total}
                  onChange={(e) => set('edition_total', e.target.value)}
                  placeholder="8"
                />
              </Field>
              <Field label="Edition available">
                <input
                  data-testid="aw-editor-edition-available"
                  type="number"
                  className={inputCls}
                  value={form.edition_available}
                  onChange={(e) => set('edition_available', e.target.value)}
                  placeholder="8"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Edition label (overrides Edition total/available on public + PDF)">
                  <input
                    data-testid="aw-editor-edition-label"
                    className={inputCls}
                    value={form.edition_label}
                    onChange={(e) => set('edition_label', e.target.value)}
                    placeholder="Open edition / Unique piece / AP available"
                  />
                </Field>
              </div>
              <Field label="Price">
                <input
                  data-testid="aw-editor-price"
                  type="number"
                  step="0.01"
                  className={inputCls}
                  value={form.price_amount}
                  onChange={(e) => set('price_amount', e.target.value)}
                  placeholder="Leave blank = 'Price on request'"
                />
              </Field>
              <Field label="Currency">
                <select
                  data-testid="aw-editor-currency"
                  className={selectCls}
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </Field>
            </div>
          </section>

          {/* Description */}
          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
            <SectionHeader title="Description" helper="Rendered on the public artwork page and inside PDF offers." />
            <textarea
              data-testid="aw-editor-description"
              className={textareaCls}
              rows={7}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="A short, evocative description of the work."
            />
            {!isNew && <CopyAssist artworkId={id} target="description" onApply={(t) => set('description', t)} />}
          </section>

          {/* Multi-image gallery — only after create */}
          {!isNew && (
            <ArtworkImagesSection
              artworkId={id}
              onPrimaryChanged={async () => {
                try {
                  const row = await getArtwork(id);
                  set('media_url', row.media_url || '');
                  set('thumbnail_url', row.thumbnail_url || '');
                } catch (_e) { /* ignore */ }
              }}
            />
          )}

          {!isNew && (
            <ArtworkStoryEditor artworkId={id} artistName={artist?.artist_name} />
          )}
        </div>

        {/* RIGHT — sticky curator rail */}
        <div className="space-y-4">
          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 sticky top-24">
            <SectionHeader title="Status" size="lg" />
            <div className="space-y-2">
              <StatusRow
                current={form.status}
                target="draft"
                label="Draft"
                helper="Only you can see this artwork."
                disabled={busy || isNew}
                onClick={() => doStatusAction(markDraft, 'Marked draft')}
              />
              <StatusRow
                current={form.status}
                target="published"
                label="Published"
                helper="Visible on the public site + PDF-ready."
                disabled={busy || isNew || !form.media_url}
                onClick={() => doStatusAction(publishArtwork, 'Published')}
              />
              <StatusRow
                current={form.status}
                target="hidden"
                label="Hidden"
                helper="Removed from public, but not archived."
                disabled={busy || isNew}
                onClick={() => doStatusAction(hideArtwork, 'Hidden')}
              />
              <StatusRow
                current={form.status}
                target="sold_out"
                label="Sold out"
                helper="Public page shows Sold Out badge."
                disabled={busy || isNew}
                onClick={() => doStatusAction(markSoldOut, 'Marked sold out')}
              />
              <StatusRow
                current={form.status}
                target="archived"
                label="Archived"
                helper="Hidden from all lists. Restorable."
                disabled={busy || isNew}
                onClick={() => doStatusAction(archiveArtwork, 'Archived')}
              />
            </div>
          </section>

          <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-5">
            <SectionHeader title="Featured" helper="Featured artworks show first on public grids." size="lg" />
            <button
              data-testid="aw-editor-featured-toggle"
              onClick={() => set('is_featured', !form.is_featured)}
              className={`w-full text-left px-3 py-2.5 border transition-colors ${form.is_featured ? 'border-[#90E0EF]/40 bg-[#90E0EF]/5 text-[#90E0EF]' : 'border-[#1a1a1a] text-[#666] hover:border-[#333] hover:text-[#f6f6f6]'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono uppercase tracking-[0.16em]">
                  {form.is_featured ? '★ Featured' : 'Not featured'}
                </span>
                <CheckCircle2 className={`w-4 h-4 ${form.is_featured ? '' : 'opacity-30'}`} />
              </div>
            </button>
          </section>

          {!isNew && (
            <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-5">
              <SectionHeader title="Timestamps" size="lg" />
              <dl className="text-[11px] font-mono space-y-1.5 text-[#777]">
                {form.__created_at && (
                  <div className="flex justify-between">
                    <dt>Created</dt>
                    <dd className="text-[#c8c8c8]">{new Date(form.__created_at).toLocaleString()}</dd>
                  </div>
                )}
                {form.__published_at && (
                  <div className="flex justify-between">
                    <dt>Published</dt>
                    <dd className="text-[#7ed4a0]">{new Date(form.__published_at).toLocaleString()}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {!isNew && (
            <section className="border border-[#e28f8f]/30 bg-[#0a0a0a] p-5" data-testid="aw-editor-danger-zone">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#e28f8f] mb-2">
                Danger zone
              </div>
              <p className="text-[11px] font-mono text-[#8a8a8a] leading-relaxed mb-3">
                Permanently remove this artwork from Supabase. Inquiries, images, and PDF offer entries will cascade.
              </p>
              <button
                type="button"
                data-testid="aw-editor-delete-permanent"
                onClick={() => setDeleteOpen(true)}
                disabled={busy}
                className="w-full text-[11px] font-mono uppercase tracking-[0.16em] px-3 py-2.5 border border-[#e28f8f]/40 text-[#e28f8f] hover:bg-[#e28f8f]/8 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete permanently
              </button>
            </section>
          )}
        </div>
      </div>

      <DeleteArtworkModal
        artworkId={id}
        artworkTitle={form.title}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          navigate('/admin/artworks', { replace: true });
        }}
      />
    </div>
  );
}

// Status radio-like row
function StatusRow({ current, target, label, helper, onClick, disabled }) {
  const active = current === target;
  const tone = STATUS_TONES[target] || STATUS_TONES.draft;
  return (
    <button
      onClick={onClick}
      disabled={disabled || active}
      data-testid={`aw-editor-status-${target}`}
      className="w-full text-left px-3 py-2.5 border transition-all duration-200 disabled:cursor-not-allowed"
      style={{
        borderColor: active ? tone.border : '#1a1a1a',
        background: active ? tone.bg : 'transparent',
        color: active ? tone.text : '#c8c8c8',
        opacity: disabled && !active ? 0.5 : 1,
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-[0.16em]">{label}</span>
        {active && <CheckCircle2 className="w-4 h-4" style={{ color: tone.text }} />}
      </div>
      <p className="text-[10px] font-mono text-[#666] mt-1">{helper}</p>
    </button>
  );
}
